import { createReducer, on } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { AssistantAction } from './assistant.action';
import { AssistantState } from './assistant.state';
import { AssistantOverlayTypeEnum } from './assistant.model';
import { AssistantSearch } from '@vi-iot/client/apis/assistant/assistant.api-model';

export const assistantInitState: AssistantState = {
  filters: {
    value: [],
    loading: false,
    loaded: false,
    error: null,
  },
  searchQuery: null,
  selectedFilters: [],
  suggestions: {
    value: [],
    loading: false,
    loaded: false,
    error: null,
  },
  searchDocuments: {
    value: [],
    totalCount: 0,
    canFetch: false,
    loading: false,
    loaded: false,
    error: null,
  },
  selectedDocument: {
    searchDocument: null,
    details: {
      value: null,
      loading: false,
      loaded: false,
      error: null,
    },
    content: {
      value: null,
      loading: false,
      loaded: false,
      error: null,
    },
    images: {
      value: null,
      loading: false,
      loaded: false,
      error: null,
    },
  },
  assistantFeedbacks: {
    filters: {
      search: null,
      filter: null,
      sortBy: null,
      startDate: null,
      endDate: null,
    },
    feedbackData: {
      value: [],
      totalCount: 0,
      canFetch: false,
      loading: false,
      loaded: false,
      error: null,
    },
  },
  overlay: { current: AssistantOverlayTypeEnum.SearchView, previous: null },
};

export const assistantReducer = createReducer(
  assistantInitState,
  on(
    AssistantAction.getFilters.attempt,
    (state, { init }): AssistantState => ({
      ...state,
      filters: {
        value: init ? [] : state.filters.value,
        loading: true,
        loaded: false,
        error: null,
      },
    })
  ),

  on(
    AssistantAction.getFilters.success,
    (state, { filters }): AssistantState => ({
      ...state,
      filters: {
        value: filters,
        loading: false,
        loaded: true,
        error: null,
      },
    })
  ),

  on(
    AssistantAction.getFilters.error,
    (state, { error }): AssistantState => ({
      ...state,
      filters: {
        value: state.filters.value,
        loading: false,
        loaded: true,
        error,
      },
    })
  ),

  on(
    AssistantAction.setSearchQuery.success,
    (state, { query }): AssistantState => ({
      ...state,
      searchQuery: query,
    })
  ),

  on(
    AssistantAction.setAssistantFilters.success,
    (state, { filters }): AssistantState => ({
      ...state,
      selectedFilters: filters,
    })
  ),

  on(
    AssistantAction.setAssistantFilters.clear,
    (state): AssistantState => ({
      ...state,
      selectedFilters: [],
      searchQuery: null,
      filters: {
        value: [],
        loading: false,
        loaded: false,
        error: null,
      },
    })
  ),

  on(
    AssistantAction.getSuggestions.attempt,
    (state): AssistantState => ({
      ...state,
      suggestions: {
        value: [],
        loading: true,
        loaded: false,
        error: null,
      },
    })
  ),

  on(
    AssistantAction.getSuggestions.success,
    (state, { suggestions }): AssistantState => ({
      ...state,
      suggestions: {
        value: suggestions,
        loading: false,
        loaded: true,
        error: null,
      },
    })
  ),

  on(
    AssistantAction.getSuggestions.error,
    (state, { error }): AssistantState => ({
      ...state,
      suggestions: {
        value: state.suggestions.value,
        loading: false,
        loaded: true,
        error,
      },
    })
  ),

  on(
    AssistantAction.searchForDocuments.attempt,
    (state): AssistantState => ({
      ...state,
      searchDocuments: {
        value: [],
        totalCount: 0,
        canFetch: false,
        loading: true,
        loaded: false,
        error: null,
      },
    })
  ),

  on(
    AssistantAction.searchForDocuments.success,
    (state, { documents, totalCount }): AssistantState => ({
      ...state,
      searchDocuments: {
        value: state.searchDocuments.value.concat(documents),
        totalCount: totalCount,
        canFetch: totalCount > documents.length,
        loading: false,
        loaded: true,
        error: null,
      },
    })
  ),

  on(
    AssistantAction.searchForDocuments.fetch,
    (state): AssistantState => ({
      ...state,
      searchDocuments: {
        ...state.searchDocuments,
        loading: true,
        error: null,
      },
    })
  ),

  on(
    AssistantAction.searchForDocuments.noDataToFetch,
    (state): AssistantState => ({
      ...state,
      searchDocuments: {
        ...state.searchDocuments,
        loading: false,
        loaded: true,
        canFetch: false,
      },
    })
  ),

  on(
    AssistantAction.searchForDocuments.error,
    (state, { error }): AssistantState => ({
      ...state,
      searchDocuments: {
        value: state.searchDocuments.value,
        totalCount: state.searchDocuments.totalCount,
        canFetch: false,
        loading: false,
        loaded: true,
        error,
      },
    })
  ),

  on(
    AssistantAction.clearSearch,
    (state): AssistantState => ({
      ...state,
      searchQuery: null,
      searchDocuments: {
        value: [],
        totalCount: 0,
        canFetch: false,
        loading: false,
        loaded: false,
        error: null,
      },
      suggestions: {
        value: [],
        loading: false,
        loaded: false,
        error: null,
      },
    })
  ),

  on(
    AssistantAction.selectDocument.setSearchDocument,
    (state, { document }): AssistantState => {
      if (!isEqual(state.selectedDocument.searchDocument, document)) {
        return {
          ...state,
          selectedDocument: {
            searchDocument: document,
            details: {
              value: null,
              loading: false,
              loaded: false,
              error: null,
            },
            content: {
              value: null,
              loading: false,
              loaded: false,
              error: null,
            },
            images: {
              value: null,
              loading: false,
              loaded: false,
              error: null,
            },
          },
        };
      }

      return { ...state };
    }
  ),

  on(
    AssistantAction.selectDocument.getDocumentDetails,
    (state): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        searchDocument: null,
        details: {
          ...state.selectedDocument.details,
          loading: true,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getDocumentDetailsAborted,
    (state): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        details: {
          ...state.selectedDocument.details,
          loading: false,
          loaded: true,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getDocumentDetailsSuccess,
    (state, { documentDetails }): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        details: {
          value: documentDetails,
          loading: false,
          loaded: true,
          error: null,
        },
        content: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
        images: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getDocumentDetailsWithUrlRedirectionSuccess,
    (state, { documentDetails }): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        // Map response to display needed data, close action navigates to initial intial /assistant page
        searchDocument: {
          ...documentDetails,
          informationType: null,
          modificationDate: null,
          subtitle: null,
          pageCount: null,
          pageNumber: null,
          additionalFields: null,
          body: null,
        } as AssistantSearch,
        details: {
          value: documentDetails,
          loading: false,
          loaded: true,
          error: null,
        },
        content: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
        images: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getDocumentDetailsError,
    (state, { error }): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        details: {
          value: null,
          loading: false,
          loaded: true,
          error: error,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getContent,
    (state): AssistantState => ({
      ...state,
      selectedDocument: {
        searchDocument: state.selectedDocument.searchDocument,
        details: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
        content: {
          value: null,
          loading: true,
          loaded: false,
          error: null,
        },
        images: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getContentByDocumentId,
    (state): AssistantState => ({
      ...state,
      selectedDocument: {
        searchDocument: null,
        details: state.selectedDocument.details,
        content: {
          value: null,
          loading: true,
          loaded: false,
          error: null,
        },
        images: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getContentSuccess,
    (state, { documentContent }): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        content: {
          value: documentContent,
          loading: false,
          loaded: true,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getContentError,
    (state, { error }): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        content: {
          value: null,
          loading: false,
          loaded: true,
          error,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getImages,
    (state): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        images: {
          value: null,
          loading: true,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getImagesAborted,
    (state): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        images: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getImagesSuccess,
    (state, { images }): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        content: {
          ...state.selectedDocument.content,
          loading: true,
        },
        images: {
          value: images,
          loading: false,
          loaded: true,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.getImagesError,
    (state, { error }): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        content: {
          ...state.selectedDocument.content,
          loading: false,
        },
        images: {
          value: null,
          loading: false,
          loaded: true,
          error: error,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.parseDocumentContent,
    (state, { documentContent }): AssistantState => ({
      ...state,
      selectedDocument: {
        ...state.selectedDocument,
        content: {
          value: documentContent,
          loading: false,
          loaded: true,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.selectDocument.clearState,
    (state): AssistantState => ({
      ...state,
      selectedDocument: {
        details: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
        searchDocument: null,
        content: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
        images: {
          value: null,
          loading: false,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.assistantFeedbackData.attempt,
    (state): AssistantState => ({
      ...state,
      assistantFeedbacks: {
        ...state.assistantFeedbacks,
        feedbackData: {
          value: [],
          totalCount: 0,
          canFetch: false,
          loading: true,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.assistantFeedbackData.fetch,
    (state): AssistantState => ({
      ...state,
      assistantFeedbacks: {
        ...state.assistantFeedbacks,
        feedbackData: {
          ...state.assistantFeedbacks.feedbackData,
          loading: true,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.assistantFeedbackData.noDataToFetch,
    (state): AssistantState => ({
      ...state,
      assistantFeedbacks: {
        ...state.assistantFeedbacks,
        feedbackData: {
          ...state.assistantFeedbacks.feedbackData,
          loading: false,
          loaded: true,
          canFetch: false,
        },
      },
    })
  ),

  on(
    AssistantAction.assistantFeedbackData.success,
    (state, { feedbackData, totalCount, limit }): AssistantState => ({
      ...state,
      assistantFeedbacks: {
        ...state.assistantFeedbacks,
        feedbackData: {
          value: state.assistantFeedbacks.feedbackData.value.concat(feedbackData),
          totalCount,
          canFetch: limit === feedbackData.length,
          loading: false,
          loaded: true,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.assistantFeedbackData.error,
    (state, { error }): AssistantState => ({
      ...state,
      assistantFeedbacks: {
        ...state.assistantFeedbacks,
        feedbackData: {
          ...state.assistantFeedbacks.feedbackData,
          loading: false,
          loaded: true,
          error,
        },
      },
    })
  ),

  on(
    AssistantAction.assistantFeedbackData.setSearch,
    (state, { search }): AssistantState => ({
      ...state,
      assistantFeedbacks: {
        ...state.assistantFeedbacks,
        filters: {
          ...state.assistantFeedbacks.filters,
          search: search,
        },
        feedbackData: {
          value: [],
          loading: false,
          loaded: false,
          canFetch: false,
          totalCount: 0,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.assistantFeedbackData.setFilterData,
    (state, { filter, startDate, endDate, sortOrder }): AssistantState => {
      const filtersState = structuredClone(state.assistantFeedbacks.filters);

      if (filter) {
        filtersState.filter = filter;
      }
      if (startDate) {
        filtersState.startDate = startDate;
      }
      if (endDate) {
        filtersState.endDate = endDate;
      }
      if (sortOrder) {
        filtersState.sortBy = sortOrder;
      }

      return {
        ...state,
        assistantFeedbacks: {
          ...state.assistantFeedbacks,
          filters: {
            ...filtersState,
          },
          feedbackData: {
            value: [],
            loading: false,
            loaded: false,
            canFetch: false,
            totalCount: 0,
            error: null,
          },
        },
      };
    }
  ),

  on(
    AssistantAction.assistantFeedbackData.clear,
    (state): AssistantState => ({
      ...state,
      assistantFeedbacks: {
        filters: {
          search: null,
          filter: null,
          sortBy: null,
          startDate: null,
          endDate: null,
        },
        feedbackData: {
          value: [],
          totalCount: 0,
          canFetch: false,
          loading: false,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    AssistantAction.changeOverlay,
    (state, { overlay }): AssistantState => ({
      ...state,
      overlay: {
        previous: state.overlay.current,
        current: overlay,
      },
    })
  ),

  on(
    AssistantAction.setInitialState,
    (): AssistantState => ({
      ...assistantInitState,
    })
  )
);
